/**
 * What the plan trains, as a ranked list: the group's name, its own track, and its share.
 *
 * ── It replaces the canon's stacked bar and its legend (owner, 16 September 2026) ─────────────
 * The canon draws one bar of six coloured segments and a wrapping key underneath. Two problems, and
 * the owner named both. The key is a second pass: the trainer reads a stripe, then hunts the same
 * hue in the legend to find out what it was, and six hues a few pixels apart make that hunt real
 * work. And the bar is the brightest object on the pane, so the loudest thing on a plan's page is
 * the one readout nobody opens the plan to see.
 *
 * Ranked rows answer the question the trainer actually has — what is this plan mostly built on —
 * in reading order, with the name beside its own bar. Nothing has to be matched by colour, so the
 * key disappears rather than moving, and with it two lines of the readout's height.
 *
 * ── The tracks keep the FULL `--mg-*` hues ───────────────────────────────────────────────────
 * An earlier cut mixed each hue 55% into the card to quieten it. The owner refused that — "bez
 * wypranych kolorów partii" — and he is right on the merits too: the hues are the one thing telling
 * the rows apart, and a washed hue at 6px of height is a grey. The quiet came from somewhere else:
 * the figure beside these rows went monochrome, so the section now carries one coloured object
 * instead of two.
 *
 * ── Every track is measured against the plan's LEADER, not against 100% ──────────────────────
 * A full-body plan's biggest group is around 24%; against a 100% track its whole ranking would be
 * six stubs in the left quarter. Against the leader the top row always fills its track and the rest
 * are read as fractions of it, which is the comparison the trainer is making anyway. The printed
 * percentage stays absolute, so nothing about the number is relative.
 *
 * ── One `role="img"` for the whole list, not a label per row ─────────────────────────────────
 * The rows are a picture of a distribution: read out one at a time they are six fragments, and the
 * tracks add nothing a screen reader can use. The list carries the spoken sentence the section
 * composes and everything inside it is hidden, exactly as the bar it replaces did.
 */

import type { CSSProperties } from 'react';

import styles from './MuscleRanking.module.css';

export interface MuscleRankingRow {
  readonly label: string;
  readonly percent: number;
  readonly colour: string;
}

interface MuscleRankingProps {
  readonly rows: readonly MuscleRankingRow[];
  /**
   * The whole readout in one sentence, for a reader that cannot see the tracks. Composed by the
   * section, which owns the copy; this file holds none.
   */
  readonly spoken: string;
  /** The share, already formatted for the trainer's locale — `model/` holds no sentences. */
  readonly formatPercent: (percent: number) => string;
  /** `useSkeletonGate`'s hand-over class. */
  readonly className?: string;
}

/**
 * How full a row's track is drawn, as a percentage of the leader's.
 *
 * The leader is read off the rows rather than assumed to be the first: the projection sorts by
 * share, but `Inne` is appended after the cut and can be larger than the smallest named group, so
 * "the first row" and "the biggest row" are not the same claim.
 */
function fillPercent(percent: number, leader: number): number {
  if (leader <= 0) return 0;
  return Math.round((percent / leader) * 100);
}

export function MuscleRanking({ rows, spoken, formatPercent, className }: MuscleRankingProps) {
  const leader = rows.reduce((max, row) => Math.max(max, row.percent), 0);

  return (
    <div
      className={[String(styles.ranking), className ?? ''].filter(Boolean).join(' ')}
      role="img"
      aria-label={spoken}
    >
      {rows.map((row, index) => (
        <div
          // The list is replaced wholesale when the plan changes and never reorders in place, so
          // the position IS the identity here.
          key={index}
          className={`${String(styles.row)} ${row.percent === leader ? String(styles.lead) : ''}`}
          aria-hidden
        >
          <span className={styles.label}>{row.label}</span>
          <span className={styles.track}>
            <span
              className={styles.fill}
              style={
                {
                  '--row-fill': `${String(fillPercent(row.percent, leader))}%`,
                  '--row-ink': row.colour,
                } as CSSProperties
              }
            />
          </span>
          <b className={styles.percent}>{formatPercent(row.percent)}</b>
        </div>
      ))}
    </div>
  );
}
